import { useState, useRef, useEffect } from 'react'
import { Check, FileText, Droplets, Heart, Wrench, Utensils, ChevronDown } from 'lucide-react'
import type { TriggerRecordType } from '../types'
import type {
  WaterQualityResponse,
  FishHealthResponse,
  MaintenanceResponse,
  FeedRecordResponse,
} from '@/shared/types/api'

type RecordData = WaterQualityResponse | FishHealthResponse | MaintenanceResponse | FeedRecordResponse

interface TriggerRecordListProps {
  recordType: TriggerRecordType
  records: RecordData[]
  selectedRecordId: string
  onSelectRecord: (recordId: string) => void
  isLoading: boolean
  disabled?: boolean
}

// Format date for display
function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-AU', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// Get the date field from different record types
function getRecordDate(record: RecordData, recordType: TriggerRecordType): string {
  switch (recordType) {
    case 'water_quality':
      return (record as WaterQualityResponse).testedAt
    case 'health_check':
      return (record as FishHealthResponse).checkedAt
    case 'feed_record':
      return (record as FeedRecordResponse).fedAt
    case 'maintenance_check':
      return (record as MaintenanceResponse).checkedAt
    default:
      return record.createdAt
  }
}

// Format water quality record summary
function formatWaterQuality(record: WaterQualityResponse): string {
  const parts: string[] = []
  if (record.dissolvedOxygen !== undefined) parts.push(`DO: ${record.dissolvedOxygen.toFixed(1)}`)
  if (record.ph !== undefined) parts.push(`pH: ${record.ph.toFixed(1)}`)
  if (record.temperature !== undefined) parts.push(`${record.temperature.toFixed(1)}°C`)
  if (record.ammonia !== undefined) parts.push(`NH₃: ${record.ammonia.toFixed(2)}`)
  return parts.length > 0 ? parts.join(', ') : 'No readings'
}

// Format health check record summary
function formatHealthCheck(record: FishHealthResponse): string {
  const parts: string[] = []
  if (record.mortalityCount !== undefined) parts.push(`Mortality: ${record.mortalityCount}`)
  if (record.overallHealth) parts.push(`Health: ${record.overallHealth}`)
  if (record.parasitesObserved) parts.push('Parasites observed')
  if (record.diseaseSymptoms) parts.push('Symptoms noted')
  return parts.length > 0 ? parts.join(', ') : 'No issues noted'
}

// Format feed record summary
function formatFeedRecord(record: FeedRecordResponse): string {
  const parts: string[] = []
  if (record.quantity !== undefined && record.unit) {
    parts.push(`${record.quantity}${record.unit}`)
  } else if (record.quantity !== undefined) {
    parts.push(`${record.quantity}kg`)
  }
  if (record.feedType) parts.push(record.feedType)
  if (record.appetite) parts.push(`Appetite: ${record.appetite}`)
  return parts.length > 0 ? parts.join(', ') : 'Feed record'
}

// Format maintenance check summary
function formatMaintenanceCheck(record: MaintenanceResponse): string {
  const parts: string[] = []
  if (record.checkType) parts.push(record.checkType)
  if (record.waterLevel !== undefined) parts.push(`Level: ${record.waterLevel}m`)
  if (record.aeratorStatus) parts.push(`Aerator: ${record.aeratorStatus}`)
  if (record.pumpStatus) parts.push(`Pump: ${record.pumpStatus}`)
  return parts.length > 0 ? parts.join(', ') : 'Maintenance check'
}

// Get summary text based on record type
function getRecordSummary(record: RecordData, recordType: TriggerRecordType): string {
  switch (recordType) {
    case 'water_quality':
      return formatWaterQuality(record as WaterQualityResponse)
    case 'health_check':
      return formatHealthCheck(record as FishHealthResponse)
    case 'feed_record':
      return formatFeedRecord(record as FeedRecordResponse)
    case 'maintenance_check':
      return formatMaintenanceCheck(record as MaintenanceResponse)
    default:
      return 'Record'
  }
}

// Get icon based on record type
function RecordIcon({ recordType }: { recordType: TriggerRecordType }) {
  const iconClass = 'h-4 w-4 text-slate-500'
  switch (recordType) {
    case 'water_quality':
      return <Droplets className={iconClass} />
    case 'health_check':
      return <Heart className={iconClass} />
    case 'feed_record':
      return <Utensils className={iconClass} />
    case 'maintenance_check':
      return <Wrench className={iconClass} />
    default:
      return <FileText className={iconClass} />
  }
}

export function TriggerRecordList({
  recordType,
  records,
  selectedRecordId,
  onSelectRecord,
  isLoading,
  disabled = false,
}: TriggerRecordListProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Find selected record
  const selectedRecord = records.find(r => r.id === selectedRecordId)

  const handleSelectRecord = (recordId: string) => {
    onSelectRecord(recordId)
    setIsOpen(false)
  }

  if (isLoading) {
    return (
      <div className="rounded-lg border border-slate-200/50 bg-slate-50 p-4">
        <div className="flex items-center justify-center gap-2 text-slate-500">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-primary-500" />
          <span className="text-sm">Loading records...</span>
        </div>
      </div>
    )
  }

  if (records.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200/50 bg-slate-50 p-4">
        <p className="text-center text-sm text-slate-500">No recent records found</p>
      </div>
    )
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown trigger button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          relative w-full rounded-lg border border-slate-200/50 bg-white px-4 py-2.5 text-left shadow-sm
          transition-all hover:border-primary-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20
          ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
        `}
      >
        <div className="flex items-center justify-between">
          {selectedRecord ? (
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <RecordIcon recordType={recordType} />
              <div className="min-w-0 flex-1">
                <span className="text-sm font-medium text-slate-700">
                  {formatDate(getRecordDate(selectedRecord, recordType))}
                </span>
                <p className="text-sm text-slate-500 truncate">
                  {getRecordSummary(selectedRecord, recordType)}
                </p>
              </div>
            </div>
          ) : (
            <span className="text-sm text-slate-400">Select a record...</span>
          )}
          <ChevronDown
            className={`h-5 w-5 text-slate-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute z-50 mt-2 w-full rounded-lg border border-slate-200/50 bg-white shadow-lg overflow-hidden">
          <div className="max-h-64 overflow-y-auto">
            {records.map((record) => {
              const isSelected = selectedRecordId === record.id
              const dateStr = getRecordDate(record, recordType)
              const summary = getRecordSummary(record, recordType)

              return (
                <button
                  key={record.id}
                  type="button"
                  onClick={() => handleSelectRecord(record.id)}
                  className={`
                    w-full flex items-start gap-3 px-4 py-3 text-left border-b border-slate-100 last:border-b-0
                    transition-colors cursor-pointer hover:bg-slate-50
                    ${isSelected ? 'bg-emerald-50' : ''}
                  `}
                >
                  {/* Selection indicator */}
                  <div
                    className={`
                      flex-shrink-0 mt-0.5 h-5 w-5 rounded border-2 flex items-center justify-center
                      ${isSelected ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300'}
                    `}
                  >
                    {isSelected && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                  </div>

                  {/* Record icon */}
                  <div className="flex-shrink-0 mt-0.5">
                    <RecordIcon recordType={recordType} />
                  </div>

                  {/* Record details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-slate-700">
                        {formatDate(dateStr)}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 truncate mt-0.5">
                      {summary}
                    </p>
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default TriggerRecordList
